import React, { useState, useEffect, useRef, useCallback } from 'react';
import { MapPin } from 'lucide-react';

// Cache module-level para não re-buscar entre remounts
let citiesCache = null;

const CityAutocomplete = ({ value, onChange, onSelect, required = false }) => {
  const [query, setQuery] = useState(value || '');
  const [cities, setCities] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const wrapperRef = useRef(null);

  // Sincronizar query com value externo (ex: geolocalização)
  useEffect(() => {
    setQuery(value || '');
  }, [value]);

  // Buscar cidades IBGE (uma vez)
  useEffect(() => {
    const fetchCities = async () => {
      if (citiesCache) {
        setCities(citiesCache);
        return;
      }
      setLoading(true);
      try {
        const res = await fetch('https://servicodados.ibge.gov.br/api/v1/localidades/municipios?orderBy=nome');
        const data = await res.json();
        const formatted = data.map(c => ({
          name: c.nome,
          uf: c.microrregiao?.mesorregiao?.UF?.sigla || '',
          label: `${c.nome} - ${c.microrregiao?.mesorregiao?.UF?.sigla || ''}`,
        }));
        citiesCache = formatted;
        setCities(formatted);
      } catch {
        // Fallback: permitir digitação livre
        setCities([]);
      } finally {
        setLoading(false);
      }
    };
    fetchCities();
  }, []);

  // Fechar dropdown ao clicar fora
  useEffect(() => {
    const handleClick = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const normalize = (str) => str.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();

  const handleInputChange = useCallback((e) => {
    const val = e.target.value;
    setQuery(val);
    onChange(val);

    if (val.length < 2 || cities.length === 0) {
      setSuggestions([]);
      setIsOpen(false);
      return;
    }

    const q = normalize(val);
    const filtered = cities
      .filter(c => normalize(c.label).includes(q))
      .slice(0, 8);
    setSuggestions(filtered);
    setIsOpen(filtered.length > 0);
  }, [cities, onChange]);

  const handleSelect = (city) => {
    setQuery(city.label);
    onChange(city.label);
    if (onSelect) onSelect(city);
    setIsOpen(false);
    setSuggestions([]);
  };

  return (
    <div ref={wrapperRef} className="relative">
      <div className="relative">
        <MapPin className="absolute left-3 top-3.5 w-4 h-4 text-slate-400" />
        <input
          type="text"
          value={query}
          onChange={handleInputChange}
          onFocus={() => { if (suggestions.length > 0) setIsOpen(true); }}
          placeholder={loading ? 'Carregando cidades...' : 'Ex: Porto Alegre - RS'}
          className="w-full pl-9 p-3 bg-slate-50 border border-slate-200 rounded-xl outline-none focus:border-blue-400 transition-colors"
          required={required}
          autoComplete="off"
        />
      </div>
      {isOpen && suggestions.length > 0 && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-slate-200 rounded-xl shadow-lg max-h-48 overflow-y-auto">
          {suggestions.map((city, i) => (
            <button
              key={i}
              type="button"
              onClick={() => handleSelect(city)}
              className="w-full text-left px-4 py-2.5 text-sm hover:bg-blue-50 hover:text-blue-700 transition-colors first:rounded-t-xl last:rounded-b-xl flex items-center gap-2"
            >
              <MapPin className="w-3 h-3 text-slate-300 shrink-0" />
              <span>{city.name}</span>
              <span className="text-slate-400 text-xs ml-auto">{city.uf}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default CityAutocomplete;
